/**
 * Assessment ICD-10 field format: "G40.909 - Epilepsy, unspecified, not intractable".
 * The secondary field holds several entries, newline-separated (older records: ", "-separated).
 * Descriptions contain commas, so entries are split where a new code starts, never at every
 * comma. Mirrors parseIcd10Entries in the backend diagnosisExtraction.js (CMS-485, claims).
 */
import type { Icd10Code } from '../dtos/reference/icd10.dto';

// A piece starts an entry when it begins with a code followed by " - " or the end of the piece.
const ENTRY_START = /^([A-Z](?:[0-9][0-9A-Z]|[A-Z][0-9])(?:\.[0-9A-Z]{1,4})?)(?:\s*-\s*(.*))?$/;

export interface Icd10Entry {
  code: string;
  description: string;
}

export function parseIcd10Entries(text: unknown): Icd10Entry[] {
  const entries: Icd10Entry[] = [];
  for (const raw of String(text ?? '').split(/[,\n]/)) {
    const piece = raw.trim();
    if (!piece) continue;
    const start = piece.match(ENTRY_START);
    if (start) entries.push({ code: start[1], description: (start[2] || '').trim() });
    else if (entries.length) {
      const last = entries[entries.length - 1];
      last.description = last.description ? `${last.description}, ${piece}` : piece;
    }
  }
  return entries;
}

export const formatIcd10Entry = (c: Pick<Icd10Code, 'code' | 'description'>): string =>
  c.description ? `${c.code} - ${c.description}` : c.code;

/** Entry strings of a stored field, one per code. */
export const splitIcd10Field = (text: unknown): string[] => parseIcd10Entries(text).map(formatIcd10Entry);

/** Stored form of a list of entry strings. */
export const joinIcd10Field = (entries: string[]): string => entries.join('\n');

/** The first entry of a stored field as a picker value, or null. */
export function icd10FromField(text: unknown): Icd10Code | null {
  const first = parseIcd10Entries(text)[0];
  return first ? { code: first.code, description: first.description, billable: true } : null;
}
